import { createContext, useContext, useMemo, type ComponentType } from 'react';
import { T, useGT } from 'gt-react';
import type { DateRange } from 'react-day-picker';
import {
  REPORT_DATE_PRESETS,
  type ReportDatePreset,
  type ReportFilter,
} from '@erp/shared-types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/common/MultiSelect';
import { DateRangePicker } from '@/components/common/date-pickers/date-range-picker';
import { useReportDatasets } from '../ReportDataContext';

/** The values a value-list filter offers for a column, and whether they are still loading. */
export type ReportFilterOptions = { values: string[]; isLoading: boolean };

export type ReportValuesControlProps = {
  filter: Extract<ReportFilter, { kind: 'values' }>;
  label: string;
  onChange: (filter: ReportFilter) => void;
};

/**
 * The control a surface shows for a value-list filter. A component, so a
 * surface can fetch the values with its own hooks (a Workbook Report past the
 * Client row limit asks the server); the default reads the datasets' rows in
 * the browser.
 */
export const ReportValuesControlContext =
  createContext<ComponentType<ReportValuesControlProps>>(DatasetValuesControl);

function DatasetValuesControl(props: ReportValuesControlProps) {
  const options = useDatasetFilterOptions(props.filter.column);
  return <ReportValuesSelect {...props} options={options} />;
}

/** A column's distinct values across every dataset that has it, as text, sorted. */
export function useDatasetFilterOptions(column: string): ReportFilterOptions {
  const { datasets } = useReportDatasets();
  return useMemo(() => {
    const seen = new Set<string>();
    for (const dataset of datasets) {
      if (!dataset.columns.some((c) => c.key === column)) continue;
      for (const row of dataset.rows) {
        const value = row[column];
        seen.add(value === null || value === undefined ? '' : String(value));
      }
    }
    const collator = new Intl.Collator(undefined, { numeric: true });
    return {
      values: [...seen].sort((a, b) => collator.compare(a, b)),
      isLoading: datasets.some((dataset) => dataset.isLoading),
    };
  }, [datasets, column]);
}

const CUSTOM = 'custom';

export function useDatePresetLabels(): Record<ReportDatePreset, string> {
  const gt = useGT();
  const context = 'report date filter preset';
  return {
    'last-7-days': gt('Last 7 days', { context }),
    'last-30-days': gt('Last 30 days', { context }),
    'last-90-days': gt('Last 90 days', { context }),
    'this-month': gt('This month', { context }),
    'last-month': gt('Last month', { context }),
    'this-quarter': gt('This quarter', { context }),
    'this-year': gt('This year', { context }),
    'last-year': gt('Last year', { context }),
  };
}

function isoDay(date: Date | undefined): string | undefined {
  if (!date) return undefined;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function localDate(day: string | undefined): Date | undefined {
  if (!day) return undefined;
  const [y = 0, m = 1, d = 1] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** A value-list filter's picker over the values a surface lists. */
export function ReportValuesSelect({
  filter,
  label,
  onChange,
  options: { values },
}: ReportValuesControlProps & { options: ReportFilterOptions }) {
  const gt = useGT();
  const empty = gt('(Empty)', { context: 'report filter empty value' });
  const option = (value: string) => ({ id: value, label: value || empty });
  // Values chosen before, or that the Result no longer has, stay listed.
  const all = [...new Set([...values, ...filter.values])];
  return (
    <MultiSelect
      size="sm"
      options={all.map(option)}
      value={filter.values.map(option)}
      onChange={(selected) =>
        onChange({ ...filter, values: selected.map((s) => s.id) })
      }
      placeholder={gt('All', { context: 'report filter nothing chosen' })}
      searchPlaceholder={gt('Search {label}…', { label })}
      hideChips
      // Already in numeric order (B2 before B10); MultiSelect's own sort is by text.
      shouldAutoSort={false}
      className="w-48"
      aria-label={label}
    />
  );
}

function DateControl({
  filter,
  onChange,
}: {
  filter: Extract<ReportFilter, { kind: 'date-range' }>;
  onChange: (filter: ReportFilter) => void;
}) {
  const gt = useGT();
  const presets = useDatePresetLabels();
  const range: DateRange | undefined =
    filter.from || filter.to
      ? { from: localDate(filter.from), to: localDate(filter.to) }
      : undefined;
  return (
    <div className="flex items-center gap-1">
      <Select
        value={filter.preset ?? CUSTOM}
        onValueChange={(next) => {
          const preset = REPORT_DATE_PRESETS.find((p) => p === next);
          onChange({ ...filter, preset });
        }}
      >
        <SelectTrigger
          className="h-8 w-36 text-xs"
          aria-label={gt('Date range', { context: 'report date filter' })}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={CUSTOM}>
            <T context="report date filter preset">Custom</T>
          </SelectItem>
          {REPORT_DATE_PRESETS.map((preset) => (
            <SelectItem key={preset} value={preset}>
              {presets[preset]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!filter.preset && (
        <DateRangePicker
          value={range}
          clearable
          className="w-56 text-xs"
          placeholder={gt('Any date', { context: 'report date filter' })}
          onChange={(next) =>
            onChange({
              ...filter,
              from: isoDay(next?.from),
              to: isoDay(next?.to),
            })
          }
        />
      )}
    </div>
  );
}

function NumberControl({
  filter,
  onChange,
}: {
  filter: Extract<ReportFilter, { kind: 'number-range' }>;
  onChange: (filter: ReportFilter) => void;
}) {
  const gt = useGT();
  const bound = (value: string) => {
    if (value.trim() === '') return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  };
  return (
    <div className="flex items-center gap-1">
      <Input
        className="h-8 w-24 text-xs"
        inputMode="decimal"
        aria-label={gt('At least', { context: 'report number filter' })}
        placeholder={gt('Min', { context: 'report number filter' })}
        defaultValue={filter.min ?? ''}
        onChange={(e) => onChange({ ...filter, min: bound(e.target.value) })}
      />
      <span className="text-xs text-muted-foreground">–</span>
      <Input
        className="h-8 w-24 text-xs"
        inputMode="decimal"
        aria-label={gt('At most', { context: 'report number filter' })}
        placeholder={gt('Max', { context: 'report number filter' })}
        defaultValue={filter.max ?? ''}
        onChange={(e) => onChange({ ...filter, max: bound(e.target.value) })}
      />
    </div>
  );
}

/**
 * The Report filters above a Report's grid: one control per filter, each
 * narrowing every component whose dataset has its column.
 */
export function ReportFilterBar({
  filters,
  onChange,
}: {
  filters: readonly ReportFilter[];
  onChange: (filters: ReportFilter[]) => void;
}) {
  const gt = useGT();
  const { datasets } = useReportDatasets();
  const ValuesControl = useContext(ReportValuesControlContext);
  if (!filters.length) return null;
  const labelOf = (filter: ReportFilter) =>
    filter.label?.trim() ||
    datasets
      .flatMap((dataset) => dataset.columns)
      .find((column) => column.key === filter.column)?.label ||
    filter.column;
  const change = (index: number) => (next: ReportFilter) =>
    onChange(filters.map((f, i) => (i === index ? next : f)));
  return (
    <div
      className="flex flex-wrap items-end gap-3"
      role="group"
      aria-label={gt('Report filters')}
    >
      {filters.map((filter, index) => {
        const label = labelOf(filter);
        return (
          <div key={filter.id} className="space-y-1">
            <Label className="text-xs text-muted-foreground">{label}</Label>
            {filter.kind === 'values' ? (
              <ValuesControl
                filter={filter}
                label={label}
                onChange={change(index)}
              />
            ) : filter.kind === 'date-range' ? (
              <DateControl filter={filter} onChange={change(index)} />
            ) : (
              <NumberControl filter={filter} onChange={change(index)} />
            )}
          </div>
        );
      })}
    </div>
  );
}
